import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import {
    FileText,
    Download,
    Check,
    Clock,
    DollarSign,
    Search,
    Filter,
    CheckCircle,
    Plus,
    X,
    Calendar,
    User as UserIcon
} from 'lucide-react';

interface Invoice {
    id: string;
    invoice_number: string;
    user_id: string;
    affiliate_details: {
        company_name: string;
        country: string;
    };
    total_amount: number;
    currency: string;
    status: 'draft' | 'approved' | 'paid';
    is_reverse_charge: boolean;
    pdf_url?: string;
    created_at: any;
}

export default function AdminInvoices() {
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const [filter, setFilter] = useState<string>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [affiliates, setAffiliates] = useState<{ id: string, name: string }[]>([]);
    const [genAffiliateId, setGenAffiliateId] = useState('');
    const [genPeriodStart, setGenPeriodStart] = useState('');
    const [genPeriodEnd, setGenPeriodEnd] = useState('');

    useEffect(() => {
        fetchInvoices();
        fetchAffiliates();
    }, [filter]);

    const fetchAffiliates = async () => {
        try {
            const q = query(collection(db, 'users'), where('role', '==', 'affiliate'));
            const snapshot = await getDocs(q);
            const list = snapshot.docs
                .map(doc => ({
                    id: doc.id,
                    name: doc.data().company_details?.name || doc.data().email
                }))
                .sort((a, b) => a.name.localeCompare(b.name));
            setAffiliates(list);
        } catch (error) {
            console.error('Error fetching affiliates:', error);
        }
    };

    const fetchInvoices = async () => {
        setLoading(true);
        try {
            let invoicesQuery;

            if (filter === 'all') {
                invoicesQuery = query(
                    collection(db, 'invoices'),
                    orderBy('created_at', 'desc')
                );
            } else {
                invoicesQuery = query(
                    collection(db, 'invoices'),
                    where('status', '==', filter),
                    orderBy('created_at', 'desc')
                );
            }

            const snapshot = await getDocs(invoicesQuery);
            const invoicesList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Invoice[];

            setInvoices(invoicesList);
        } catch (error) {
            console.error('Error fetching invoices:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (invoiceId: string, invoiceNumber: string) => {
        if (!confirm(`Are you sure you want to approve invoice ${invoiceNumber}? This will send an email notification to the affiliate.`)) {
            return;
        }

        setActionLoading(invoiceId);
        try {
            const approveInvoiceFunc = httpsCallable(functions, 'approveInvoice');
            await approveInvoiceFunc({ invoice_id: invoiceId });

            setSuccessMessage(`Invoice ${invoiceNumber} approved successfully!`);
            setTimeout(() => setSuccessMessage(''), 3000);

            // Refresh list
            fetchInvoices();
        } catch (error) {
            console.error('Error approving invoice:', error);
            alert('Failed to approve invoice. Please try again.');
        } finally {
            setActionLoading(null);
        }
    };

    const handleMarkPaid = async (invoiceId: string, invoiceNumber: string) => {
        if (!confirm(`Mark invoice ${invoiceNumber} as paid? This indicates payment has been sent.`)) {
            return;
        }

        setActionLoading(invoiceId);
        try {
            const markPaidFunc = httpsCallable(functions, 'markInvoicePaid');
            await markPaidFunc({ invoice_id: invoiceId });

            setSuccessMessage(`Invoice ${invoiceNumber} marked as paid!`);
            setTimeout(() => setSuccessMessage(''), 3000);

            fetchInvoices();
        } catch (error) {
            console.error('Error marking invoice as paid:', error);
            alert('Failed to mark invoice as paid. Please try again.');
        } finally {
            setActionLoading(null);
        }
    };

    const handleGenerateInvoices = async () => {
        setGenerating(true);
        try {
            const generateInvoicesFunc = httpsCallable(functions, 'generateInvoices');
            const result = await generateInvoicesFunc({
                affiliate_id: genAffiliateId || undefined,
                period_start: genPeriodStart || undefined,
                period_end: genPeriodEnd || undefined
            });

            const data = result.data as any;
            if (data.success) {
                setSuccessMessage(data.message || 'Invoices generated successfully!');
                setTimeout(() => setSuccessMessage(''), 5000);
                setShowGenerateModal(false);
                fetchInvoices();
                // Reset form
                setGenAffiliateId('');
                setGenPeriodStart('');
                setGenPeriodEnd('');
            } else {
                alert(data.message || 'Failed to generate invoices');
            }
        } catch (error: any) {
            console.error('Error generating invoices:', error);
            alert(`Error: ${error.message || 'Failed to generate invoices'}`);
        } finally {
            setGenerating(false);
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'draft':
                return <span className="badge badge-warning">Draft</span>;
            case 'approved':
                return <span className="badge badge-info">Approved</span>;
            case 'paid':
                return <span className="badge badge-success">Paid</span>;
            default:
                return <span className="badge">{status}</span>;
        }
    };

    const formatDate = (timestamp: any) => {
        if (!timestamp) return '-';
        return timestamp.toDate().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const filteredInvoices = invoices.filter(invoice => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
            invoice.invoice_number.toLowerCase().includes(term) ||
            invoice.affiliate_details.company_name.toLowerCase().includes(term)
        );
    });

    const draftCount = invoices.filter(i => i.status === 'draft').length;
    const approvedCount = invoices.filter(i => i.status === 'approved').length;

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white">Invoice Management</h1>
                    <p className="text-gray-400">Review, approve, and track affiliate invoices</p>
                </div>
                <button
                    onClick={() => setShowGenerateModal(true)}
                    className="btn-primary flex items-center gap-2"
                >
                    <Plus size={20} />
                    Generate Invoices
                </button>
            </div>

            {/* Success Message */}
            {successMessage && (
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 flex items-center gap-3">
                    <CheckCircle className="text-emerald-400" size={20} />
                    <span className="text-emerald-400">{successMessage}</span>
                </div>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="stat-card flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                        <Clock className="text-amber-400" size={24} />
                    </div>
                    <div>
                        <p className="text-gray-400 text-sm">Pending Review</p>
                        <p className="text-2xl font-bold text-white">{draftCount}</p>
                    </div>
                </div>
                <div className="stat-card flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                        <Check className="text-blue-400" size={24} />
                    </div>
                    <div>
                        <p className="text-gray-400 text-sm">Awaiting Payment</p>
                        <p className="text-2xl font-bold text-white">{approvedCount}</p>
                    </div>
                </div>
                <div className="stat-card flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                        <DollarSign className="text-emerald-400" size={24} />
                    </div>
                    <div>
                        <p className="text-gray-400 text-sm">Total Invoices</p>
                        <p className="text-2xl font-bold text-white">{invoices.length}</p>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="card">
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                        <input
                            type="text"
                            placeholder="Search by invoice number or company..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="input-field pl-12"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <Filter size={20} className="text-gray-400" />
                        <select
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            className="input-field py-2 px-3 w-auto"
                        >
                            <option value="all">All Invoices</option>
                            <option value="draft">Draft Only</option>
                            <option value="approved">Approved Only</option>
                            <option value="paid">Paid Only</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Invoices Table */}
            <div className="card">
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : filteredInvoices.length === 0 ? (
                    <div className="text-center py-12">
                        <FileText className="mx-auto text-gray-500 mb-4" size={48} />
                        <h3 className="text-lg font-semibold text-white mb-2">No Invoices Found</h3>
                        <p className="text-gray-400">
                            {filter !== 'all' ? 'Try changing the filter.' : 'No invoices have been generated yet.'}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="text-left text-gray-400 text-sm border-b border-white/10">
                                    <th className="pb-4 font-medium">Invoice</th>
                                    <th className="pb-4 font-medium">Affiliate</th>
                                    <th className="pb-4 font-medium">Amount</th>
                                    <th className="pb-4 font-medium">Status</th>
                                    <th className="pb-4 font-medium">Date</th>
                                    <th className="pb-4 font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredInvoices.map((invoice) => (
                                    <tr key={invoice.id} className="table-row">
                                        <td className="py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center">
                                                    <FileText className="text-primary-400" size={18} />
                                                </div>
                                                <div>
                                                    <span className="font-mono text-white">{invoice.invoice_number}</span>
                                                    {invoice.is_reverse_charge && (
                                                        <span className="ml-2 text-xs text-amber-400">RC</span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-4">
                                            <div>
                                                <p className="text-white">{invoice.affiliate_details.company_name}</p>
                                                <p className="text-sm text-gray-400">{invoice.affiliate_details.country}</p>
                                            </div>
                                        </td>
                                        <td className="py-4">
                                            <span className="text-white font-semibold">
                                                €{invoice.total_amount.toFixed(2)}
                                            </span>
                                        </td>
                                        <td className="py-4">
                                            {getStatusBadge(invoice.status)}
                                        </td>
                                        <td className="py-4 text-gray-400">
                                            {formatDate(invoice.created_at)}
                                        </td>
                                        <td className="py-4">
                                            <div className="flex items-center gap-2">
                                                {invoice.status === 'draft' && (
                                                    <button
                                                        onClick={() => handleApprove(invoice.id, invoice.invoice_number)}
                                                        disabled={actionLoading === invoice.id}
                                                        className="btn-primary py-2 px-4 text-sm flex items-center gap-2"
                                                    >
                                                        {actionLoading === invoice.id ? (
                                                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                        ) : (
                                                            <>
                                                                <Check size={16} />
                                                                Approve
                                                            </>
                                                        )}
                                                    </button>
                                                )}
                                                {invoice.status === 'approved' && (
                                                    <button
                                                        onClick={() => handleMarkPaid(invoice.id, invoice.invoice_number)}
                                                        disabled={actionLoading === invoice.id}
                                                        className="btn-secondary py-2 px-4 text-sm flex items-center gap-2 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                                                    >
                                                        {actionLoading === invoice.id ? (
                                                            <div className="w-4 h-4 border-2 border-emerald-400/30 border-t-emerald-400 rounded-full animate-spin" />
                                                        ) : (
                                                            <>
                                                                <DollarSign size={16} />
                                                                Mark Paid
                                                            </>
                                                        )}
                                                    </button>
                                                )}
                                                {invoice.pdf_url && (
                                                    <a
                                                        href={invoice.pdf_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                                                        title="Download PDF"
                                                    >
                                                        <Download size={18} className="text-gray-400" />
                                                    </a>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Generate Invoices Modal */}
            {showGenerateModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="glass-dark rounded-2xl p-6 w-full max-w-md animate-slide-up border border-white/10">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center">
                                    <FileText className="text-primary-400" size={20} />
                                </div>
                                <h2 className="text-xl font-bold text-white">Generate Invoices</h2>
                            </div>
                            <button
                                onClick={() => setShowGenerateModal(false)}
                                className="text-gray-400 hover:text-white p-2 hover:bg-white/5 rounded-lg transition-colors"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                                <p className="text-xs text-amber-300 flex items-start gap-2">
                                    <Clock size={16} className="shrink-0 mt-0.5" />
                                    This will search for pending conversions that are not yet linked to an invoice and create draft invoices for them.
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                                    <UserIcon size={14} />
                                    Specific Affiliate (Optional)
                                </label>
                                <select
                                    value={genAffiliateId}
                                    onChange={(e) => setGenAffiliateId(e.target.value)}
                                    className="input-field"
                                >
                                    <option value="">All Affiliates</option>
                                    {affiliates.map(aff => (
                                        <option key={aff.id} value={aff.id}>{aff.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                                        <Calendar size={14} />
                                        Start Date
                                    </label>
                                    <input
                                        type="date"
                                        value={genPeriodStart}
                                        onChange={(e) => setGenPeriodStart(e.target.value)}
                                        className="input-field"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                                        <Calendar size={14} />
                                        End Date
                                    </label>
                                    <input
                                        type="date"
                                        value={genPeriodEnd}
                                        onChange={(e) => setGenPeriodEnd(e.target.value)}
                                        className="input-field"
                                    />
                                </div>
                            </div>

                            <div className="pt-4 flex gap-3">
                                <button
                                    onClick={() => setShowGenerateModal(false)}
                                    className="btn-secondary flex-1"
                                    disabled={generating}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleGenerateInvoices}
                                    disabled={generating}
                                    className="btn-primary flex-1 flex items-center justify-center gap-2 py-3"
                                >
                                    {generating ? (
                                        <>
                                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                            Generating...
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle size={18} />
                                            Generate
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
